import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { Check, Sparkles, AlertTriangle } from 'lucide-react';
import { ABLE_COLORS } from '../constants';

// Toast Context
const ToastContext = createContext(null);

// Celebration messages for task completion
const CELEBRATION_MESSAGES = [
  'Done!',
  'Got it!',
  'Nice work!',
  'Checked off!',
  'One down!',
  'Progress!',
  'Crushed it!'
];

// Get a random celebration message
function getRandomCelebration() {
  return CELEBRATION_MESSAGES[Math.floor(Math.random() * CELEBRATION_MESSAGES.length)];
}

/**
 * useToast - Hook to show toast messages
 */
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

/**
 * ToastProvider - Wrap app to enable toasts
 */
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, options = {}) => {
    const id = Date.now();

    // Handle celebration type specially
    const isCelebration = options.type === 'celebration';
    const isError = options.type === 'error';
    const displayMessage = isCelebration ? getRandomCelebration() : message;

    const defaultDuration = isCelebration ? 2500 : (isError ? 5000 : 2000);
    const toast = {
      id,
      message: displayMessage,
      type: options.type || 'success',
      duration: options.duration || defaultDuration
    };

    setToasts(prev => [...prev, toast]);

    // Auto-remove after duration
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, toast.duration);
  }, []);

  const hideToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      {children}
      <ToastContainer toasts={toasts} onHide={hideToast} />
    </ToastContext.Provider>
  );
}

/**
 * ToastContainer - Renders active toasts
 */
function ToastContainer({ toasts, onHide }) {
  return (
    <div style={{
      position: 'fixed',
      bottom: '20px',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      pointerEvents: 'none'
    }}>
      {toasts.map(toast => (
        <ToastItem key={toast.id} toast={toast} onHide={() => onHide(toast.id)} />
      ))}
    </div>
  );
}

/**
 * ToastItem - Individual toast message
 */
function ToastItem({ toast, onHide }) {
  const [isVisible, setIsVisible] = useState(false);
  const isCelebration = toast.type === 'celebration';
  const isError = toast.type === 'error';

  useEffect(() => {
    // Animate in
    requestAnimationFrame(() => setIsVisible(true));

    // Animate out before removal
    const timer = setTimeout(() => {
      setIsVisible(false);
    }, toast.duration - 200);

    return () => clearTimeout(timer);
  }, [toast.duration]);

  // Choose icon based on type
  const Icon = isError ? AlertTriangle : (isCelebration ? Sparkles : Check);

  // Error gets a red background; celebration gets a warm accent.
  const bgColor = isError ? '#B8475A' : (isCelebration ? ABLE_COLORS.accent : ABLE_COLORS.primary);

  return (
    <div
      style={{
        background: bgColor,
        color: 'white',
        padding: isCelebration ? '12px 20px' : '10px 16px',
        borderRadius: '20px',
        fontSize: isCelebration ? '15px' : '14px',
        fontWeight: isCelebration ? '600' : '500',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        boxShadow: isCelebration 
          ? '0 4px 16px rgba(0,0,0,0.2)' 
          : '0 4px 12px rgba(0,0,0,0.15)',
        opacity: isVisible ? 1 : 0,
        transform: isVisible 
          ? 'translateY(0) scale(1)' 
          : 'translateY(10px) scale(0.95)',
        transition: 'all 0.25s ease',
        pointerEvents: 'auto'
      }}
      onClick={onHide}
    >
      <Icon size={isCelebration ? 18 : 16} />
      {toast.message}
    </div>
  );
}

export default ToastProvider;
